/**
 * Lab limits (F7.5-b). uc-core src/modules/lab/dto/lab.dto.ts CreateLabLimitDto: cropId? (omit = every
 * crop), panel ≤40 (stored upper case), analyte? ≤80 (omit = panel default), rule NOT_DETECTED |
 * MAX_LIMIT, limitValue? / maxLoq? decimal strings with ≤4 decimals (MAX_LIMIT needs limitValue,
 * 422 LIMIT_INCOMPLETE), uom ≤10, requiredForRelease?, source ≤120, effectiveFrom (date).
 *
 * Rows are never edited: a change is a new row, the next version for the same crop / panel /
 * analyte. A report row is judged by the limit in force on its test date (lab-evaluation.util.ts):
 * crop row before all-crops row, analyte row before the panel default, then latest effective_from,
 * then highest version.
 */

export type LabLimitRule = 'NOT_DETECTED' | 'MAX_LIMIT'

export interface LabLimitDraft {
  /** '' = every crop */
  cropId: string
  panel: string
  /** '' = panel default */
  analyte: string
  rule: LabLimitRule
  limitValue: string
  maxLoq: string
  uom: string
  requiredForRelease: boolean
  source: string
  effectiveFrom: string
}

export interface LabLimitProblems {
  panel?: string
  analyte?: string
  limitValue?: string
  maxLoq?: string
  uom?: string
  source?: string
  effectiveFrom?: string
}

const DECIMAL = /^\d+(\.\d{1,4})?$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export const emptyLabLimitDraft = (today: string): LabLimitDraft => ({
  cropId: '',
  panel: '',
  analyte: '',
  rule: 'NOT_DETECTED',
  limitValue: '',
  maxLoq: '',
  uom: 'mg/kg',
  requiredForRelease: false,
  source: '',
  effectiveFrom: today,
})

export interface LabLimitLike {
  cropId: string | null
  panel: string
  analyte: string | null
  rule: string
  limitValue: string | null
  maxLoq: string | null
  uom: string
  requiredForRelease: boolean
  source: string
}

/** "Change" a limit: the same key prefilled for its next version, effective today. */
export function draftFromLabLimit(l: LabLimitLike, today: string): LabLimitDraft {
  return {
    cropId: l.cropId ?? '',
    panel: l.panel,
    analyte: l.analyte ?? '',
    rule: l.rule === 'MAX_LIMIT' ? 'MAX_LIMIT' : 'NOT_DETECTED',
    limitValue: trimDecimal(l.limitValue),
    maxLoq: trimDecimal(l.maxLoq),
    uom: l.uom,
    requiredForRelease: l.requiredForRelease,
    source: l.source,
    effectiveFrom: today,
  }
}

/** "0.0100" → "0.01"; null → "". */
export function trimDecimal(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const s = String(v)
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s
}

/** Panels are stored trimmed and upper case (as uc-core does), so "pesticide " = PESTICIDE. */
export const normalisePanel = (s: string) => s.trim().toUpperCase()

const decimalProblem = (v: string): string | undefined => {
  const t = v.trim()
  if (!DECIMAL.test(t)) return 'A number with at most 4 decimals, e.g. 0.01'
  return undefined
}

export function validateLabLimitDraft(d: LabLimitDraft): LabLimitProblems {
  const p: LabLimitProblems = {}
  const panel = d.panel.trim()
  if (!panel) p.panel = 'Enter the panel, e.g. PESTICIDE'
  else if (panel.length > 40) p.panel = 'At most 40 characters'
  if (d.analyte.trim().length > 80) p.analyte = 'At most 80 characters'
  if (d.rule === 'MAX_LIMIT') {
    if (!d.limitValue.trim()) p.limitValue = 'Enter the limit'
    else p.limitValue = decimalProblem(d.limitValue)
  } else if (d.limitValue.trim()) {
    p.limitValue = decimalProblem(d.limitValue)
  }
  if (d.maxLoq.trim()) p.maxLoq = decimalProblem(d.maxLoq)
  const uom = d.uom.trim()
  if (!uom) p.uom = 'Enter the unit, e.g. mg/kg'
  else if (uom.length > 10) p.uom = 'At most 10 characters'
  const source = d.source.trim()
  if (!source) p.source = 'Say where the limit comes from'
  else if (source.length > 120) p.source = 'At most 120 characters'
  if (!DATE_RE.test(d.effectiveFrom) || Number.isNaN(Date.parse(d.effectiveFrom)))
    p.effectiveFrom = 'Pick the date it applies from'
  for (const k of Object.keys(p) as Array<keyof LabLimitProblems>) if (p[k] === undefined) delete p[k]
  return p
}

export const labLimitOk = (p: LabLimitProblems) => Object.keys(p).length === 0

/** POST /lab-limits body. Call only when valid. */
export function createLabLimitBody(d: LabLimitDraft) {
  const body: {
    cropId?: string
    panel: string
    analyte?: string
    rule: LabLimitRule
    limitValue?: string
    maxLoq?: string
    uom: string
    requiredForRelease: boolean
    source: string
    effectiveFrom: string
  } = {
    panel: normalisePanel(d.panel),
    rule: d.rule,
    uom: d.uom.trim(),
    requiredForRelease: d.requiredForRelease,
    source: d.source.trim(),
    effectiveFrom: d.effectiveFrom,
  }
  if (d.cropId) body.cropId = d.cropId
  if (d.analyte.trim()) body.analyte = d.analyte.trim()
  if (d.limitValue.trim()) body.limitValue = d.limitValue.trim()
  if (d.maxLoq.trim()) body.maxLoq = d.maxLoq.trim()
  return body
}

// ---------------- which version is in force ----------------

export type LimitState = 'IN_FORCE' | 'SCHEDULED' | 'SUPERSEDED'

export interface LimitKeyRow {
  id: string
  cropId: string | null
  panel: string
  analyte: string | null
  version: number
  effectiveFrom: string
}

export const limitKey = (r: Pick<LimitKeyRow, 'cropId' | 'panel' | 'analyte'>) =>
  `${r.cropId ?? '*'}|${r.panel.toUpperCase()}|${(r.analyte ?? '').trim().toLowerCase()}`

/**
 * State of each live row on `today`: per crop / panel / analyte, the row with the latest
 * effective date on or before today (highest version on a tie) is IN_FORCE, later-dated rows are
 * SCHEDULED, the rest SUPERSEDED. Only rows of the same key are compared (the panel default and an
 * analyte row both stay in force; the analyte row wins for its analyte when a report is judged).
 */
export function limitStates(rows: readonly LimitKeyRow[], today: string): Map<string, LimitState> {
  const out = new Map<string, LimitState>()
  const best = new Map<string, LimitKeyRow>()
  for (const r of rows) {
    const from = r.effectiveFrom.slice(0, 10)
    if (from > today) {
      out.set(r.id, 'SCHEDULED')
      continue
    }
    const k = limitKey(r)
    const cur = best.get(k)
    const curFrom = cur?.effectiveFrom.slice(0, 10)
    if (!cur || from > curFrom! || (from === curFrom && r.version > cur.version)) best.set(k, r)
  }
  const inForce = new Set([...best.values()].map((r) => r.id))
  for (const r of rows) if (!out.has(r.id)) out.set(r.id, inForce.has(r.id) ? 'IN_FORCE' : 'SUPERSEDED')
  return out
}
